import axios from 'axios'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import { store } from '../../../src'
import { logout } from '../../store/actions'

const server = axios.create({
  baseURL: 'https://pickup-logistics.johnabrahamtosin.workers.dev/api/v1',
  headers: { 'Content-Type': 'application/json' },
})

// Track if we're currently refreshing to avoid multiple simultaneous refresh attempts
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

server.interceptors.request.use((config) => {
  let tokenData = Cookies.get('tokenData')
  console.log('📦 tokenData from cookie:', tokenData);
  
  if (tokenData) {
    try {
      tokenData = JSON.parse(tokenData)
      console.log('✅ Parsed tokenData:', tokenData);
      
      // The token is directly in the authData object
      const token = tokenData.token
      console.log('🔑 Extracted token:', token);
      
      if (token) {
        // Set headers directly on config.headers, not config.headers.common
        config.headers['Authorization'] = `Bearer ${token}`
        config.headers['x-api-key'] = '585a93dc-9e48-4788-9ebe-57e8c16a5bc1'
        console.log('✅ Headers set - Authorization:', config.headers['Authorization']);
      } else {
        console.warn('⚠️ No token found in tokenData');
      }
    } catch (error) {
      console.error('❌ Error parsing tokenData:', error);
      Cookies.remove('tokenData')
    }
  } else {
    console.log('❌ No tokenData cookie found');
  }
  
  console.log('📤 Final request headers:', config.headers);
  return config
})

// Track if we're already handling a logout to prevent multiple redirects
let isLoggingOut = false

server.interceptors.response.use(
  (response) => {
    // Log successful actions to audit system
    if (response.config && response.config.method && response.config.url) {
      // Import audit logger dynamically to avoid circular dependencies
      import('../../utils/auditLogger').then(({ logAction }) => {
        logAction(
          response.config.method.toUpperCase(),
          response.config.url,
          response.config.data ? JSON.parse(response.config.data || '{}') : null,
          response.data?.data || response.data
        );
      }).catch(() => {
        // Silently fail if audit logging fails
      });
    }
    
    return response.data
  },
  async (err) => {
    const originalRequest = err.config

    // If error is 401 (Unauthorized) and we haven't tried to refresh yet
    if (err.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token
          return server(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Import token manager to avoid circular dependencies
        const { refreshAccessToken } = await import('../../utils/tokenManager')
        const newAuthData = await refreshAccessToken()

        if (newAuthData && newAuthData.token) {
          // Update the failed request with new token
          originalRequest.headers['Authorization'] = 'Bearer ' + newAuthData.token
          processQueue(null, newAuthData.token)
          isRefreshing = false
          
          // Retry the original request
          return server(originalRequest)
        } else {
          // Refresh failed - logout
          processQueue(new Error('Token refresh failed'), null)
          isRefreshing = false
          
          if (!isLoggingOut) {
            isLoggingOut = true
            store.dispatch(logout())
            toast.error('Session expired. Please login again.')
          }
          
          return Promise.reject(err)
        }
      } catch (refreshError) {
        processQueue(refreshError, null)
        isRefreshing = false
        
        if (!isLoggingOut) {
          isLoggingOut = true
          store.dispatch(logout())
          toast.error('Session expired. Please login again.')
        }
        
        return Promise.reject(refreshError)
      }
    }
    
    // Log other errors for debugging
    if (err.response) {
      console.error('API Error:', {
        status: err.response.status,
        url: err.config?.url,
        message: err.response.data?.message || err.message
      })
    }
    
    // Just reject the error, let the calling code handle it
    return Promise.reject(err)
  },
)

export default server